import { useQuery } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";
import { MOCK_ORDERS } from "../pages/ordersStorybookMocks";

const MOCK_ORDERS_FLAG_KEY = "wwgStoreUseMockOrdersData";

function shouldUseMockOrders() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(MOCK_ORDERS_FLAG_KEY) === "true";
}

function normalizeOrderItem(item, index, orderId) {
  if (typeof item === "string") {
    return {
      stockId: `${orderId}-item-${index}`,
      title: `Item ${index + 1}`,
      imageUrl: item,
      memberPrice: 0,
      retailPrice: null,
      itemType: null,
      author: null,
      color: null,
      size: null,
      quantity: 1,
    };
  }

  const listId = item?.listId ?? item?.setId ?? null;

  return {
    stockId: item?.stockId ?? `${orderId}-item-${index}`,
    title: item?.title ?? item?.itemDescription ?? `Item ${index + 1}`,
    imageUrl: item?.imageUrl ?? item?.path ?? "",
    memberPrice: item?.memberPrice ?? item?.price ?? item?.unitPrice ?? 0,
    retailPrice: item?.retailPrice ?? null,
    itemType: item?.itemType ?? null,
    author: item?.author ?? null,
    color: item?.color ?? null,
    size: item?.size ?? null,
    quantity: item?.quantity ?? item?.qty ?? 1,
    cartItemId: item?.cartItemId ?? null,
    productId: item?.productId ?? null,
    variationId: item?.variationId ?? null,
    variantId: item?.variantId ?? null,
    options: Array.isArray(item?.options) ? item.options : [],
    listId,
    setId: listId,
  };
}

function normalizeOrder(rawOrder) {
  const id = String(
    rawOrder?.id ??
      rawOrder?.orderId ??
      rawOrder?.orderNumber ??
      rawOrder?.checkoutSessionId ??
      ""
  );

  const normalizedItems = Array.isArray(rawOrder?.items)
    ? rawOrder.items.map((item, index) => normalizeOrderItem(item, index, id))
    : (rawOrder?.orderItems ?? []).map((item, index) =>
        normalizeOrderItem(item, index, id)
      );

  const rawBillingAddress =
    rawOrder?.billingAddress ?? rawOrder?.shipAddress ?? {};
  const normalizedBillingAddress = {
    street:
      rawBillingAddress?.street ??
      rawBillingAddress?.address1 ??
      rawBillingAddress?.line1 ??
      null,
    city: rawBillingAddress?.city ?? null,
    state: rawBillingAddress?.state ?? null,
    zip: rawBillingAddress?.zip ?? rawBillingAddress?.postalCode ?? null,
    country: rawBillingAddress?.country ?? null,
  };

  const rawPaymentMethod = rawOrder?.paymentMethod ?? null;
  const rawLast4 =
    rawOrder?.cardLastFour ??
    rawOrder?.cardLast4 ??
    rawPaymentMethod?.last4 ??
    rawPaymentMethod?.lastFour ??
    null;
  const rawCardType =
    rawOrder?.cardType ?? rawPaymentMethod?.type ?? rawPaymentMethod?.cardType;

  const hasPaymentData = Boolean(rawLast4 || rawCardType || rawPaymentMethod);
  const normalizedPaymentMethod = hasPaymentData
    ? {
        ...(rawPaymentMethod ?? {}),
        type: rawCardType ?? "Card",
        last4: rawLast4,
      }
    : null;

  return {
    ...rawOrder,
    id,
    source: rawOrder?.source ?? "legacy",
    date: rawOrder?.date ?? rawOrder?.orderDate ?? rawOrder?.createdOn ?? null,
    tracking:
      rawOrder?.tracking ??
      rawOrder?.trackingNumber ??
      rawOrder?.orderTracking?.trackingNumber ??
      "N/A",
    subtotal: rawOrder?.subtotal ?? rawOrder?.subTotal ?? 0,
    tax: rawOrder?.tax ?? rawOrder?.taxTotal ?? 0,
    shipping: rawOrder?.shipping ?? rawOrder?.shippingTotal ?? 0,
    total: rawOrder?.total ?? rawOrder?.orderTotal ?? 0,
    isPreOrder: Boolean(rawOrder?.isPreOrder),
    listId: rawOrder?.listId ?? rawOrder?.setId ?? null,
    setId: rawOrder?.listId ?? rawOrder?.setId ?? null,
    items: normalizedItems,
    billingAddress: normalizedBillingAddress,
    paymentMethod: normalizedPaymentMethod,
  };
}

export function useOrders(params = {}) {
  const useMockOrders = shouldUseMockOrders();

  return useQuery(["orders", params, useMockOrders], async () => {
    if (useMockOrders) {
      return MOCK_ORDERS.map((order) => normalizeOrder(order));
    }
    const query = new URLSearchParams(params).toString();
    const res = await makeAuthedRequest(`/orders${query ? `?${query}` : ""}`);
    if (!res.ok) throw await parseApiError(res, "Orders fetch failed");
    const orders = await res.json();
    return orders.map((order) => normalizeOrder(order));
  });
}

export function useOrder(orderId, options = {}) {
  const useMockOrders = shouldUseMockOrders();
  const isNew = Boolean(options?.isNew);

  return useQuery(
    ["order", orderId, isNew, useMockOrders],
    async () => {
      if (useMockOrders) {
        return (
          MOCK_ORDERS.map((order) => normalizeOrder(order)).find(
            (o) =>
              String(o.id) === String(orderId) ||
              String(o.checkoutSessionId ?? "") === String(orderId)
          ) ?? null
        );
      }

      const res = await makeAuthedRequest(
        `/orders/${orderId}${isNew ? "?isNew=true" : ""}`
      );
      if (!res.ok) throw await parseApiError(res, "Order fetch failed");
      const order = await res.json();
      return normalizeOrder(order);
    },
    { enabled: !!orderId }
  );
}
